import type { PatientRecord } from './patients-contracts'
import type { InteractionOperationalState } from '../conversations/interaction-operational-contracts'

export type PatientListItem = PatientRecord
/** What Add patient found: an exact existing practice record, or a new one. */
export type PatientAddOutcome = 'MATCH' | 'CREATED'
export type PatientFilter =
  'all' | 'attention' | 'upcoming' | 'recent' | 'new' | 'existing'
export type ServerPatientFilter = Exclude<PatientFilter, 'attention'>

export const patientFilters: Array<{
  value: ServerPatientFilter
  label: string
}> = [
  { value: 'all', label: 'All patients' },
  { value: 'upcoming', label: 'Upcoming visit' },
  { value: 'recent', label: 'Recently engaged' },
  { value: 'new', label: 'New' },
  // No "Existing" filter (product owner, 2026-10-18): All patients already
  // includes them. The server still accepts filter=existing.
]

export type PatientOperationalStates = Record<
  string,
  InteractionOperationalState
>

export function patientOperationalState(
  patient: PatientRecord,
  states: PatientOperationalStates
) {
  return patient.interactionAggregateId
    ? states[patient.interactionAggregateId]
    : undefined
}

export function patientNeedsAttention(
  patient: PatientRecord,
  states: PatientOperationalStates = {}
) {
  const operational = patientOperationalState(patient, states)
  return (
    patient.linkageState.includes('REVIEW') ||
    operational?.reviewStatus === 'NEEDS_REVIEW' ||
    operational?.needsAction === true
  )
}

export function nextPatientAppointment(
  patient: PatientRecord,
  now = Date.now()
) {
  return patient.appointments
    .filter(
      (appointment) =>
        appointment.scheduledAtMs !== null &&
        appointment.scheduledAtMs >= now &&
        !appointment.status.toLocaleLowerCase('en').includes('cancel')
    )
    .sort(
      (left, right) =>
        (left.scheduledAtMs ?? Number.MAX_SAFE_INTEGER) -
        (right.scheduledAtMs ?? Number.MAX_SAFE_INTEGER)
    )[0]
}

export function comparePatients(left: PatientRecord, right: PatientRecord) {
  return (
    left.displayName.localeCompare(right.displayName, 'en', {
      sensitivity: 'base',
    }) || left.patientAuthorityId.localeCompare(right.patientAuthorityId)
  )
}

export function filterPatients(
  patients: PatientRecord[],
  query: string,
  filter: PatientFilter = 'all',
  operationalStates: PatientOperationalStates = {},
  now = Date.now()
) {
  const normalized = query.trim().toLocaleLowerCase('en')
  return patients.filter((patient) => {
    const filterMatch =
      filter === 'all' ||
      (filter === 'new' && patient.recordStatus === 'NEW') ||
      (filter === 'existing' && patient.recordStatus === 'EXISTING') ||
      (filter === 'attention' &&
        patientNeedsAttention(patient, operationalStates)) ||
      (filter === 'upcoming' &&
        Boolean(nextPatientAppointment(patient, now))) ||
      (filter === 'recent' && patient.latestEngagement !== null)
    return (
      filterMatch &&
      (!normalized ||
        [
          patient.displayName,
          patient.phone,
          patient.email,
          patient.providerPatientId,
        ].some((value) => value?.toLocaleLowerCase('en').includes(normalized)))
    )
  })
}
